import Component from 'flarum/common/Component';
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import markdownToHtml from '../utils/markdown';
import QuizPanel from './QuizPanel';
// m is available globally as 'm' (Mithril) via Flarum

export default class ArticleReaderPage extends Component {
  static isPage = true;

  oninit(vnode) {
    super.oninit(vnode);
    this.article = null;
    this.quizQuestions = [];
    this.loading = true;
    this.error = null;
    this.quizPassed = false;
    this.completing = false;
    this.completed = false;
    this.completeMsg = null;
    this.assignmentQuestions = [];
    this.assignmentStatus = null;
    this.assignmentAnswers = {};
    this.submittingAssignment = false;

    this.loadArticle();
  }

  async loadArticle() {
    const id = m.route.param('id');
    if (!id) {
      this.error = '文章 ID 无效';
      this.loading = false;
      return;
    }

    try {
      const apiUrl = app.forum.attribute('apiUrl');
      const res = await app.request({
        method: 'GET',
        url: `${apiUrl}/taro/cms/articles/${id}`,
      });

      const models = app.store.pushPayload(res);
      this.article = Array.isArray(models) ? models[0] : models;

      // Extract quiz questions from the included data
      // Flarum's store.push returns the primary model but included resources
      // are accessible through the relationships
      if (this.article.quizQuestions) {
        const qs = this.article.quizQuestions();
        this.quizQuestions = qs || [];
      }

      // 加载作业题 + 作业状态
      this.loadAssignmentData(id);
    } catch (e) {
      this.error = e.message || '加载文章失败';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  async loadAssignmentData(articleId) {
    const apiUrl = app.forum.attribute('apiUrl');
    try {
      const qRes = await app.request({ method: 'GET', url: `${apiUrl}/taro/admin/assignment-questions?article_id=${articleId}` });
      this.assignmentQuestions = qRes.data || [];
    } catch (e) {}
    try {
      const aRes = await app.request({ method: 'GET', url: `${apiUrl}/taro/courses/${articleId}/assignment` });
      this.assignmentStatus = aRes.data ? aRes.data.attributes : null;
      if (this.assignmentStatus && this.assignmentStatus.answer) {
        try { this.assignmentAnswers = JSON.parse(this.assignmentStatus.answer); } catch (_) { this.assignmentAnswers = {}; }
      }
    } catch (e) { this.assignmentStatus = null; }
    m.redraw();
  }

  async submitAssignment() {
    if (this.submittingAssignment || this.assignmentQuestions.length === 0) return;
    this.submittingAssignment = true;
    m.redraw();
    const apiUrl = app.forum.attribute('apiUrl');
    const articleId = this.article.id();
    const answers = {};
    this.assignmentQuestions.forEach((q) => { answers[q.id] = this.assignmentAnswers[q.id] || ''; });
    try {
      const res = await app.request({
        method: 'POST',
        url: `${apiUrl}/taro/courses/${articleId}/assignment`,
        body: { data: { attributes: { answer: answers } } },
      });
      this.assignmentStatus = res.data ? res.data.attributes : null;
      if (this.assignmentStatus && this.assignmentStatus.status === 'grading') {
        setTimeout(() => this.refreshAssignmentStatus(articleId), 3000);
      }
    } catch (e) {
      const body = e.response ? JSON.parse(e.response) : {};
      this.completeMsg = body.errors?.[0]?.detail || '提交失败';
    } finally {
      this.submittingAssignment = false;
      m.redraw();
    }
  }

  async refreshAssignmentStatus(articleId) {
    const apiUrl = app.forum.attribute('apiUrl');
    try {
      const res = await app.request({ method: 'GET', url: `${apiUrl}/taro/courses/${articleId}/assignment` });
      this.assignmentStatus = res.data ? res.data.attributes : null;
      if (this.assignmentStatus && this.assignmentStatus.status === 'grading') {
        setTimeout(() => this.refreshAssignmentStatus(articleId), 3000);
      }
    } catch (e) {}
    m.redraw();
  }

  handleQuizComplete(passed) {
    this.quizPassed = passed;
    m.redraw();
  }

  canComplete() {
    const article = this.article;
    if (!article) return false;

    if (article.hasQuiz() && !this.quizPassed) return false;

    // 作业必须通过（所有题 roses>=2，status=passed）
    if (this.assignmentQuestions.length > 0) {
      if (!this.assignmentStatus || this.assignmentStatus.status !== 'passed') {
        return false;
      }
    }

    return true;
  }

  async handleComplete() {
    if (this.completing || !this.canComplete()) return;

    this.completing = true;
    this.completeMsg = null;

    try {
      const apiUrl = app.forum.attribute('apiUrl');
      const res = await app.request({
        method: 'POST',
        url: `${apiUrl}/taro/cms/articles/${this.article.id()}/complete`,
      });

      this.completed = true;
      this.completeMsg = '已完成此文章的学习！';

      // Update store if response contains data
      if (res.data) {
        app.store.pushPayload(res);
      }
    } catch (e) {
      const body = e.response ? JSON.parse(e.response) : {};
      this.completeMsg = body.errors?.[0]?.detail || '操作失败';
    } finally {
      this.completing = false;
      m.redraw();
    }
  }

  // ─── 侧边导航栏 ──────────────────────────────

  sidebarNav() {
    const navItems = [
      { route: 'index', icon: 'far fa-comments', label: '全部主题' },
      { route: 'courses', icon: 'fas fa-book-open', label: '课程' },
      { route: 'courseLibrary', icon: 'fas fa-book-reader', label: '课程中心' },
      { route: 'exam', icon: 'fas fa-graduation-cap', label: '考试认证' },
      { route: 'tags', icon: 'fas fa-th-large', label: '标签' },
    ];

    const currentPath = m.route.get();
    const activeItem = navItems.find(item => {
      const itemPath = app.route(item.route).replace(/\/$/, '');
      if (item.route === 'index') return currentPath === '/' || currentPath === '/index' || currentPath === '';
      return currentPath.startsWith(itemPath);
    }) || navItems[0];

    return (
      <nav className="IndexPage-nav sideNav">
        <ul>
          <li className="item-newDiscussion App-primaryControl">
            <Button className="Button Button--primary IndexPage-newDiscussion" icon="fas fa-edit" onclick={() => app.composer.open()}>
              发布主题
            </Button>
          </li>
          <li className="item-nav">
            <div className="ButtonGroup Dropdown dropdown App-titleControl Dropdown--select itemCount{navItems.length}">
              <button className="Dropdown-toggle Button" data-toggle="dropdown">
                <span className="Button-label">{activeItem.label}</span>
                <i className="icon fas fa-sort Button-caret"></i>
              </button>
              <ul className="Dropdown-menu dropdown-menu">
                {navItems.map(item => {
                  const itemPath = app.route(item.route).replace(/\/$/, '');
                  const isActive = currentPath === itemPath || (item.route === 'index' && currentPath === '/');
                  return (
                    <li className={'item-' + item.route + (isActive ? ' active' : '')}>
                      <a className="hasIcon" href={itemPath}>
                        <i className={'icon ' + item.icon + ' Button-icon'}></i>
                        <span className="Button-label">{item.label}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    );
  }

  // ─── 页面主体内容 ─────────────────────────────

  pageContent() {
    if (this.loading) {
      return <LoadingIndicator />;
    }

    if (this.error) {
      return (
        <div className="ArticleReaderPage">
          <button className="ArticleReaderPage-back" onclick={() => this.goBack()}>
            <i className="fas fa-arrow-left" /> 返回
          </button>
          <div className="error-banner">{this.error}</div>
        </div>
      );
    }

    if (!this.article) {
      return (
        <div className="ArticleReaderPage">
          <button className="ArticleReaderPage-back" onclick={() => this.goBack()}>
            <i className="fas fa-arrow-left" /> 返回
          </button>
          <div className="error-banner">文章未找到</div>
        </div>
      );
    }

    const article = this.article;
    const contentHtml = markdownToHtml(article.content() || '');
    const hasQuiz = article.hasQuiz();
    const hasAssignment = this.assignmentQuestions.length > 0;
    const swordReward = article.swordReward();

    return (
      <div className="ArticleReaderPage">
        {/* Back button */}
        <button className="ArticleReaderPage-back" onclick={() => this.goBack()}>
          <i className="fas fa-arrow-left" /> 返回课程
        </button>

        {/* Header */}
        <div className="ArticleReaderPage-header">
          <h1 className="ArticleReaderPage-title">{article.title()}</h1>
          <div className="ArticleReaderPage-meta">
            {article.level() && <span><i className="fas fa-layer-group" /> {article.level().name()}</span>}
            {article.module() && <span><i className="fas fa-folder" /> {article.module().title()}</span>}
            {swordReward > 0 && (
              <span>
                <i className="fas fa-shield-alt" /> 完成奖励 {swordReward} 宝剑
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div
          className="ArticleReaderPage-content"
          data-article-id={String(article.id())}
          data-article-title={String(article.title() || '')}
        >
          {m.trust(contentHtml)}
        </div>

        {/* Quiz Section */}
        {hasQuiz && this.quizQuestions.length > 0 && (
          <div className="ArticleReaderPage-quizSection">
            <h3 className="ArticleReaderPage-quizTitle">
              <i className="fas fa-question-circle" /> 理论测验
            </h3>
            <QuizPanel
              article={article}
              questions={this.quizQuestions}
              onComplete={(passed) => this.handleQuizComplete(passed)}
            />
          </div>
        )}

        {/* Assignment Section */}
        {hasAssignment && (
          <div className="ArticleReaderPage-assignmentSection">
            <h3 className="ArticleReaderPage-assignmentTitle">
              <i className="fas fa-pen" /> 课后作业
            </h3>
            {this.assignmentQuestions.length > 0 ? (
              <div>
                {this.assignmentQuestions.map((q, i) => {
                  const qa = q.attributes || q;
                  const qid = q.id;
                  const roses = this.assignmentStatus && this.assignmentStatus.roses ? (this.assignmentStatus.roses[qid] ?? this.assignmentStatus.roses[String(qid)]) : null;
                  const fb = this.assignmentStatus && this.assignmentStatus.ai_feedback ? (this.assignmentStatus.ai_feedback[qid] ?? this.assignmentStatus.ai_feedback[String(qid)]) : null;
                  const readOnly = this.submittingAssignment || (this.assignmentStatus && (this.assignmentStatus.status === 'grading' || this.assignmentStatus.status === 'passed'));
                  return (
                    <div className="AssignmentQuestion-item" key={qid}>
                      <div className="AssignmentQuestion-prompt"><strong>题{i + 1}：</strong>{qa.prompt}</div>
                      <textarea
                        className="FormControl AssignmentQuestion-textarea"
                        rows="4"
                        placeholder="请输入你的答案…"
                        value={this.assignmentAnswers[qid] || ''}
                        oninput={(e) => { this.assignmentAnswers[qid] = e.target.value; m.redraw(); }}
                        disabled={readOnly}
                      />
                      {roses !== null && roses !== undefined && (
                        <div className="AssignmentQuestion-result">
                          <span className="AssignmentQuestion-roses">{roses > 0 ? '🌹'.repeat(roses) : '❌'}（{roses}朵）</span>
                          {fb && <span className="AssignmentQuestion-feedback">{fb}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {this.assignmentStatus && this.assignmentStatus.status === 'grading' && (
                  <div className="AssignmentQuestion-grading"><i className="fas fa-spinner fa-pulse" /> AI 批改中，请稍候...</div>
                )}
                {this.assignmentStatus && this.assignmentStatus.status === 'passed' && (
                  <div className="AssignmentQuestion-passed">✅ 作业通过！可标记完成获得宝剑积分</div>
                )}
                {this.assignmentStatus && this.assignmentStatus.status === 'failed' && (
                  <div className="AssignmentQuestion-failed">❌ 未通过（需所有题≥2朵玫瑰花），修改后可重新提交</div>
                )}
                {(!this.assignmentStatus || ['submitted', 'failed'].includes(this.assignmentStatus.status)) && (
                  <button
                    className="Button Button--primary"
                    onclick={() => this.submitAssignment()}
                    disabled={this.submittingAssignment}
                  >
                    {this.submittingAssignment ? <span><i className="fas fa-spinner fa-pulse" /> 提交中...</span> : <span><i className="fas fa-paper-plane" /> 提交作业 AI 批改</span>}
                  </button>
                )}
              </div>
            ) : (
              <p>该文章未配置作业题</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="ArticleReaderPage-actions">
          {!this.completed ? (
            <button
              className="Button Button--primary ArticleReaderPage-completeBtn"
              onclick={() => this.handleComplete()}
              disabled={this.completing || !this.canComplete()}
            >
              {this.completing ? (
                <span><i className="fas fa-spinner fa-pulse" /> 处理中...</span>
              ) : !this.canComplete() && hasQuiz && !this.quizPassed ? (
                <span><i className="fas fa-lock" /> 请先通过测验</span>
              ) : (
                <span><i className="fas fa-check" /> 标记完成{swordReward > 0 ? `（+${swordReward} 宝剑）` : ''}</span>
              )}
            </button>
          ) : (
            <div className="status-box success">
              <i className="fas fa-check-circle" />
              {this.completeMsg || '已完成'}
            </div>
          )}
        </div>

        {/* Status message */}
        {this.completeMsg && !this.completed && (
          <div className="message-strip error">
            {this.completeMsg}
          </div>
        )}
      </div>
    );
  }

  // ─── View ─────────────────────────────────────

  view() {
    return (
      <div className="IndexPage">
        <div className="container">
          <div className="sideNavContainer">
            {this.sidebarNav()}
            <div className="IndexPage-results sideNavOffset">
              {this.pageContent()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  goBack() {
    // Navigate back in browser history, fall back to course library
    if (window.history.length > 1) {
      window.history.back();
    } else {
      m.route.set(app.route('courseLibrary'));
    }
  }
}
